import type { ImportField } from './import-fields';

/**
 * The columns of each importable entity.
 *
 * Split from `import-fields.ts` purely on length — that file is the registry and
 * the helpers, this is the data. The `aliases` on each field are the header
 * spellings the auto-matcher accepts, normalised the same way header cells are,
 * and they are where most of the value is: a rate list whose column is called
 * PARAMETERS maps on the first try instead of needing an explanation.
 */

export const LAB_TEST_FIELDS: readonly ImportField[] = [
  {
    key: 'name',
    label: 'Test name',
    kind: 'text',
    required: true,
    aliases: [
      'parameters',
      'parameter',
      'test',
      'tests',
      'test name',
      'description',
      'particulars',
    ],
    // Their RATE LIST calls this column PARAMETERS even though the rows are
    // whole tests. Matching it is the difference between a mapping that lands
    // on the first try and one that needs an explanation.
  },
  {
    key: 'code',
    label: 'Code',
    kind: 'text',
    required: false,
    aliases: ['test code', 'short code', 'abbr', 'abbreviation'],
    help: 'Left blank, a code is derived from the name. Codes are what a re-import matches on, so a real one is worth having.',
  },
  {
    key: 'price',
    label: 'Price',
    kind: 'money',
    required: true,
    aliases: ['rate', 'rates', 'charges', 'amount', 'fee', 'fees', 'mrp'],
  },
  {
    key: 'department',
    label: 'Department',
    kind: 'text',
    required: false,
    aliases: ['dept', 'bench', 'section', 'category'],
    help: 'Usually not a column at all — it comes from the heading rows between the blocks of tests.',
  },
  {
    key: 'turnaround_hours',
    label: 'Turnaround (hours)',
    kind: 'integer',
    required: false,
    aliases: ['tat', 'turnaround', 'reporting time', 'delivery time', 'hours'],
  },
  {
    key: 'comment',
    label: 'Note',
    kind: 'text',
    required: false,
    aliases: ['remarks', 'remark', 'note', 'notes', 'description'],
  },
];

export const REAGENT_ITEM_FIELDS: readonly ImportField[] = [
  {
    key: 'name',
    label: 'Reagent name',
    kind: 'text',
    required: true,
    aliases: [
      'name',
      'item',
      'item name',
      'reagent',
      'parameters',
      'parameter',
      'description',
      'product',
    ],
  },
  {
    key: 'code',
    label: 'Code',
    kind: 'text',
    required: false,
    aliases: ['item code', 'sku', 'cat no', 'catalogue no', 'catalog no', 'ref'],
    help: 'Left blank, a code is derived from the name.',
  },
  {
    key: 'department',
    label: 'Department',
    kind: 'text',
    required: true,
    aliases: ['dept', 'bench', 'section'],
  },
  {
    key: 'base_unit',
    label: 'Counted in',
    kind: 'enum',
    required: false,
    aliases: ['unit', 'uom', 'units', 'measure'],
    options: ['ul', 'ml', 'l', 'mg', 'g', 'test', 'piece', 'vial'],
    help: 'Defaults to "test" — a kit counted in tests, which is how most of the shelf is counted.',
  },
  {
    key: 'pack_label',
    label: 'Pack',
    kind: 'text',
    required: false,
    aliases: ['pack size', 'packing', 'presentation', 'kit size'],
    help: 'Free text for the bench: "1 × 50 mL bottle", "100 tests/kit".',
  },
  {
    key: 'pack_size_base',
    label: 'Units in one pack',
    kind: 'decimal',
    required: true,
    aliases: ['pack size base', 'units per pack', 'qty per pack', 'tests per kit', 'kit size'],
    help: 'The one conversion in the whole subsystem. 50 mL of a reagent counted in µL is 50000.',
  },
  {
    key: 'reorder_level_base',
    label: 'Reorder level',
    kind: 'decimal',
    required: false,
    aliases: ['reorder', 'min stock', 'minimum', 'reorder point', 'safety stock'],
  },
  {
    key: 'opening_packs',
    label: 'Opening stock (packs)',
    kind: 'integer',
    required: false,
    aliases: ['opening', 'opening stock', 'stock', 'on hand', 'quantity', 'qty', 'packs'],
    help: 'Whole packs on the shelf today. Posted through the ledger as an opening balance, so it reconciles like every later movement.',
  },
  {
    key: 'lot_no',
    label: 'Lot number',
    kind: 'text',
    required: false,
    aliases: ['lot', 'batch', 'batch no', 'lot no'],
  },
  {
    key: 'expiry_at',
    label: 'Expiry',
    kind: 'date',
    required: false,
    aliases: ['expiry', 'exp', 'exp date', 'expires', 'expiry date', 'use by'],
  },
  {
    key: 'pack_price',
    label: 'Price per pack',
    kind: 'money',
    required: false,
    aliases: ['price', 'rate', 'cost', 'unit price', 'purchase price'],
  },
  {
    key: 'storage_location',
    label: 'Stored at',
    kind: 'text',
    required: false,
    aliases: ['location', 'storage', 'shelf', 'fridge'],
  },
  {
    key: 'note',
    label: 'Note',
    kind: 'text',
    required: false,
    aliases: ['remarks', 'remark', 'notes', 'notification'],
  },
];

export const PATIENT_FIELDS: readonly ImportField[] = [
  {
    key: 'legacy_key',
    label: 'Sheet reference',
    kind: 'text',
    required: false,
    aliases: ['patient id', 'id', 'sr no', 's no', 'serial', 'ref', 'reference', 'record no'],
    help: 'Whatever identifies this visit in the sheet. This — not the PIN — is what a second upload of the same file matches on, so mapping it is what makes a re-import safe.',
  },
  {
    key: 'name',
    label: 'Patient name',
    kind: 'text',
    required: true,
    aliases: ['name', 'patient', 'patient name', 'full name'],
  },
  {
    key: 'gender',
    label: 'Gender',
    kind: 'enum',
    required: false,
    aliases: ['sex', 'g'],
    options: ['male', 'female', 'other'],
  },
  {
    key: 'age_year',
    label: 'Age',
    kind: 'integer',
    required: false,
    aliases: ['age years', 'years', 'yrs', 'age y'],
    help: '"45 Y", "45y" and "45 years" all read as 45.',
  },
  {
    key: 'phone_number',
    label: 'Contact',
    kind: 'text',
    required: false,
    aliases: ['phone', 'mobile', 'cell', 'contact no', 'phone no', 'contact number'],
  },
  {
    key: 'refer_by',
    label: 'Referred by',
    kind: 'text',
    required: false,
    aliases: ['refer by', 'referred by', 'referrer', 'doctor', 'consultant', 'ref by'],
  },
  {
    key: 'cnic_number',
    label: 'CNIC',
    kind: 'text',
    required: false,
    aliases: ['nic', 'cnic', 'id card', 'nic no'],
  },
  {
    key: 'email',
    label: 'Email',
    kind: 'text',
    required: false,
    aliases: ['e-mail', 'mail'],
  },
  {
    key: 'pin_no',
    label: 'PIN',
    kind: 'text',
    required: false,
    aliases: ['pin', 'pin no', 'pin number', 'token'],
    help: "The sheet's own PIN is written through verbatim. It cannot collide with a desk PIN, which this app always prefixes.",
  },
  {
    key: 'tests',
    label: 'Tests charged',
    kind: 'test_list',
    required: false,
    aliases: ['test charged', 'tests charged', 'test', 'tests', 'investigations', 'procedure'],
    help: 'Split on commas only — never on "/" or "+", which appear inside real test names like Na/K and A+B.',
  },
  {
    key: 'charges',
    label: 'Charges',
    kind: 'money',
    required: false,
    aliases: ['total charges', 'gross', 'amount', 'bill', 'subtotal'],
    help: 'What was billed before discount. On a visit that resolves to exactly one test, this becomes that test’s price, so historical revenue stays historical.',
  },
  {
    key: 'discount_rupees',
    // Spelled out rather than "(Rs)" and "(%)": `normaliseName` strips
    // punctuation, so those two labels both normalise towards "discount" and
    // the percentage field won the exact-label pass — reading a column of
    // rupees as a column of percentages.
    label: 'Discount in rupees',
    kind: 'money',
    required: false,
    aliases: ['discount', 'disc', 'discount rs', 'discount amount', 'less'],
  },
  {
    key: 'discount_percentage',
    label: 'Discount percent',
    kind: 'percent',
    required: false,
    aliases: ['disc %', 'discount percent', 'discount percentage', 'disc percent'],
  },
  {
    key: 'paid',
    label: 'Paid',
    kind: 'money',
    required: false,
    aliases: ['received', 'amount paid', 'cash', 'payment'],
  },
  {
    key: 'balance',
    label: 'Balance',
    kind: 'money',
    required: false,
    aliases: ['due', 'outstanding', 'remaining', 'balance due'],
    help: 'Checked, not stored. The balance is derived from charges, discount and paid; a sheet that disagrees gets a warning rather than being silently overruled.',
  },
  {
    key: 'visit_date',
    label: 'Visit date',
    kind: 'date',
    required: false,
    aliases: ['date', 'reg date', 'registration date', 'entry date', 'test date'],
  },
  {
    key: 'year',
    label: 'Year',
    kind: 'integer',
    required: false,
    aliases: ['yr', 'financial year', 'fy'],
    help: 'Used only when there is no visit date. The visit is dated 1 July of that year and flagged approximate, so nothing implies a day nobody knows.',
  },
  {
    key: 'note',
    label: 'Note',
    kind: 'text',
    required: false,
    aliases: ['remarks', 'remark', 'notes', 'comment'],
  },
];

export const REPORT_RESULT_FIELDS: readonly ImportField[] = [
  {
    key: 'patient_key',
    label: 'Patient reference',
    kind: 'text',
    required: true,
    aliases: ['patient id', 'id', 'pin', 'pin no', 'mr no', 'ref', 'sheet reference'],
    help: 'Matched against the sheet reference an imported visit was given, then against the PIN.',
  },
  {
    key: 'test',
    label: 'Test',
    kind: 'text',
    required: true,
    aliases: ['test name', 'investigation', 'profile', 'panel'],
  },
  {
    key: 'parameter',
    label: 'Parameter',
    kind: 'text',
    required: true,
    aliases: ['analyte', 'parameter name', 'component', 'item'],
  },
  {
    key: 'result',
    label: 'Result',
    kind: 'text',
    required: true,
    aliases: ['value', 'observed value', 'reading', 'obs value'],
    help: 'Text, not a number. "Negative", "< 0.5" and "1.2" are all real results and none of them survives being parsed to a float.',
  },
  {
    key: 'unit',
    label: 'Unit',
    kind: 'text',
    required: false,
    aliases: ['units', 'uom'],
  },
  {
    key: 'normal_range',
    label: 'Reference range',
    kind: 'text',
    required: false,
    aliases: ['range', 'reference', 'normal value', 'ref range', 'biological ref interval'],
    help: 'Stored against this result as its own snapshot, so an old value is never re-judged against today’s range.',
  },
  {
    key: 'visit_date',
    label: 'Visit date',
    kind: 'date',
    required: false,
    aliases: ['date', 'test date', 'reported on'],
    help: 'Picks which visit this belongs to when the patient has more than one for the same test.',
  },
];
